import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Eye, Heart, MousePointerClick, TrendingUp, Users, FileText } from "lucide-react";

export function BrokerReports() {
  // Mock data - In production, this would come from PostHog API
  const pageViewsData = [
    { name: 'Mon', views: 120 },
    { name: 'Tue', views: 180 },
    { name: 'Wed', views: 150 },
    { name: 'Thu', views: 220 },
    { name: 'Fri', views: 280 },
    { name: 'Sat', views: 190 },
    { name: 'Sun', views: 160 },
  ];

  const listingEngagementData = [
    { name: 'Views', value: 850 },
    { name: 'Swipes', value: 420 },
    { name: 'Favorites', value: 180 },
    { name: 'Leads', value: 95 },
  ];

  const swipeData = [
    { name: 'Liked', value: 65, color: '#22c55e' },
    { name: 'Passed', value: 35, color: '#ef4444' },
  ];

  const leadsOverTimeData = [
    { name: 'Week 1', leads: 12 },
    { name: 'Week 2', leads: 19 },
    { name: 'Week 3', leads: 15 },
    { name: 'Week 4', leads: 25 },
  ];

  const stats = [
    { title: 'Total Page Views', value: '1,450', icon: Eye, change: '+12.5%', positive: true },
    { title: 'Total Favorites', value: '180', icon: Heart, change: '+8.3%', positive: true },
    { title: 'Total Swipes', value: '420', icon: MousePointerClick, change: '+15.2%', positive: true },
    { title: 'Total Leads', value: '95', icon: FileText, change: '+22.1%', positive: true },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports & Analytics</h1>
        <p className="text-muted-foreground mt-2">Track your business performance and user engagement</p>
      </div>

      {/* Key Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className={`text-xs ${stat.positive ? 'text-green-600' : 'text-red-600'} mt-1`}>
                  {stat.change} from last period
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Page Views (Last 7 Days)</CardTitle>
                <CardDescription>Daily website traffic</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={pageViewsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="views" stroke="hsl(var(--primary))" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Listing Engagement</CardTitle>
                <CardDescription>User interaction breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={listingEngagementData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="engagement" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Swipe Distribution</CardTitle>
                <CardDescription>Liked vs Passed ratio</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={swipeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {swipeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Performing Listings</CardTitle>
                <CardDescription>Most viewed listings this month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { title: 'Cafe - Inner City', views: 245, leads: 12 },
                    { title: 'Tech Startup - SaaS', views: 198, leads: 18 },
                    { title: 'Restaurant - Fine Dining', views: 176, leads: 9 },
                    { title: 'Retail - Fashion Store', views: 134, leads: 7 },
                  ].map((listing) => (
                    <div key={listing.title} className="flex items-center justify-between border-b pb-2">
                      <div>
                        <p className="font-medium">{listing.title}</p>
                        <p className="text-sm text-muted-foreground">{listing.views} views</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-primary">{listing.leads} leads</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="leads" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Leads Over Time</CardTitle>
              <CardDescription>Monthly lead generation trends</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={leadsOverTimeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="leads" stroke="#22c55e" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lead Sources</CardTitle>
              <CardDescription>Where your leads are coming from</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { source: 'Direct Listing View', count: 42, percentage: 44 },
                  { source: 'Swipe Feature', count: 28, percentage: 29 },
                  { source: 'Explore Page', count: 18, percentage: 19 },
                  { source: 'Favorites', count: 7, percentage: 8 },
                ].map((source) => (
                  <div key={source.source} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{source.source}</p>
                      <p className="text-sm text-muted-foreground">{source.count} leads ({source.percentage}%)</p>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${source.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Notice about live data */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground text-center">
            📊 This dashboard shows sample data. Live analytics are being tracked with PostHog and will populate automatically as users interact with your listings.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
